import UI from "@mile-meeting/mile-ui";
import { getKorDate, getDay, getTimeString } from "utils/date";
import styled from "@emotion/styled";
import { TRestrictRequest } from "apis/meetings/block-request";
import { useEffect, useRef, useState } from "react";

type Props = {
  data: TRestrictRequest;
  onClick: () => void;
  onApproval: () => Promise<void>;
  onReject: () => void;
};

const TableRow = ({ data, onClick, onApproval, onReject }: Props) => {
  const [approvalBtnLoading, setApprovalBtnLoading] = useState<boolean>(false);
  const requestReasonRef = useRef<HTMLDivElement>(null);
  const [reasonTooltipWidth, setReasonTooltipWidth] = useState<number>(0);

  useEffect(() => {
    const el = requestReasonRef.current;
    if (!el) return;
    const update = () => setReasonTooltipWidth(el.offsetWidth);
    update();
    const ro = new ResizeObserver(() => update());
    ro.observe(el);

    return () => ro.disconnect();
  }, []);

  return (
    <Container onClick={onClick}>
      <div
        className="meetingRoom"
        style={{
          display: "flex",
          flexDirection: "column",
          width: 266,
          gap: 4,
        }}
      >
        <UI.Typography type="body01 normal" fontStyle="bold" color="var(--alias-color-label-neutral)">
          {data.meetingRoom.name}
        </UI.Typography>
        <div className="location" style={{ display: "flex", gap: 8 }}>
          <div className="label" style={{ display: "flex", gap: 4, alignItems: "center" }}>
            <UI.Icon name="office" size={16} color="var(--alias-color-label-assistive)" />
            <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
              위치
            </UI.Typography>
          </div>
          <UI.Typography type="label01" fontStyle="regular">
            {data.meetingRoom.building} - {data.meetingRoom.floor}
          </UI.Typography>
        </div>
      </div>
      <div style={{ width: 180 }}>
        <UI.Typography type="label01" fontStyle="regular">
          {getKorDate(data.startDateTime.toString()).replaceAll(".", ". ")} ({getDay(data.startDateTime.toString())}){" "}
          {getTimeString(data.startDateTime.toString())} <br />-{" "}
          {getKorDate(data.endDateTime.toString()).replaceAll(".", ". ")} ({getDay(data.endDateTime.toString())}){" "}
          {getTimeString(data.endDateTime.toString())}
        </UI.Typography>
      </div>
      <TooltipContainer ref={requestReasonRef} style={{ flex: 1 }}>
        <UI.ToolTip
          styles={{
            zIndex: "auto",
          }}
          position={{ top: "100%", left: 0 }}
          type="white"
          text={
            <p style={{ width: reasonTooltipWidth ? `${reasonTooltipWidth}px` : undefined }}>{data.requestReason}</p>
          }
        >
          <TwoLineEllipsis>
            <UI.Typography type="label01" fontStyle="regular">
              {data.requestReason}
            </UI.Typography>
          </TwoLineEllipsis>
        </UI.ToolTip>
      </TooltipContainer>
      <TooltipContainer style={{ width: 80 }}>
        <UI.ToolTip
          styles={{
            zIndex: "auto",
          }}
          type="white"
          position={{ top: "100%", left: 0 }}
          text={
            <p>
              {data.requestBy.nickname}
              <br />({data.requestBy.email})
            </p>
          }
        >
          <UI.Typography type="label01" fontStyle="regular">
            {data.requestBy?.nickname}
          </UI.Typography>
        </UI.ToolTip>
      </TooltipContainer>
      <div style={{ width: 156, display: "flex", gap: 8 }}>
        <div
          onClick={async (e) => {
            e.stopPropagation();
            if (approvalBtnLoading) return;
            if (data.endDateTime < new Date()) return;
            setApprovalBtnLoading(true);
            await onApproval();
            setApprovalBtnLoading(false);
          }}
        >
          <UI.Button
            title="승인하기"
            primary={false}
            size="small"
            style={{ whiteSpace: "nowrap" }}
            disabled={approvalBtnLoading || data.endDateTime < new Date()}
          />
        </div>
        <div
          onClick={(e) => {
            e.stopPropagation();
            onReject();
          }}
        >
          <UI.Button type="outlined" title="반려하기" primary size="small" style={{ whiteSpace: "nowrap" }} />
        </div>
      </div>
    </Container>
  );
};

export default TableRow;

const Container = styled.div`
  display: flex;
  padding: 12px 24px;
  align-items: center;
  border-bottom: 1px solid var(--alias-color-line-normal);
  gap: 20px;
  cursor: pointer;
  &:hover {
    background-color: var(--alias-color-background-normal-alternative);
  }
`;

const TwoLineEllipsis = styled.div`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  flex: 1;
`;

const TooltipContainer = styled.div`
  .tooltip {
    z-index: 100;
  }
`;
